import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Observable, Subscription, forkJoin } from "rxjs";
import { tap, finalize } from "rxjs/operators";
//import { GridOptions, ColDef } from '@ag-grid-enterprise/all-modules';
import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { DomainDefinitionService } from "../../../builder/services/domain-definition.service";
import { GridFormatterService } from '../../../common/services/grid-formatter.service';
import { ApplicationConfigService, ModuleConfig } from "../../../services/application-config.service";
import { CasingTypeModel, DrillingEquipmentModel } from "../../../form-controls/components/new-casing-plan/models/casing-plan.model";
import { Well, CasingTubing, EditedCasingTubingRecord } from '../../../common/models/well.model';
import { IconActionRendererComponent } from "../../../common/components/ag-grid/icon-action-renderer.component";
import { EditCasingTubingRecordComponent } from "../edit-casing-tubing-record/edit-casing-tubing-record.component";

@Component({
    selector: 'well-detail-casing-strings',
    templateUrl: './well-detail-casing-strings.component.html',
    styleUrls: ['./well-detail-casing-strings.component.scss'],
    standalone: false
})
export class WellDetailCasingStringsComponent implements OnInit, OnDestroy {

	_allCasingPlanStrings: CasingTypeModel[] = [];
	_validStringCasingTypeCodes = ['DP*', 'COND', 'MS1', 'SURF', 'I1', 'I2', 'PROD', 'PROD2', 'HOL1', 'PKR', 'L1', 'T1'];
	_stringSubscription: Subscription = Subscription.EMPTY;

	config: ModuleConfig;
	currentCasings: CasingTubing[] = [];

	dataReady: boolean = false;
	filteredCasings: CasingTypeModel[] = [];
	formBuilt: boolean = true;

	@Output() casingTubingUpdated: EventEmitter<EditedCasingTubingRecord> = new EventEmitter<EditedCasingTubingRecord>();
	@Input() canEdit: boolean;

	@ViewChild(EditCasingTubingRecordComponent) public editor: EditCasingTubingRecordComponent;

	gridOptions: GridOptions;
	gridApi?: GridApi;

	rowEditActive: number | null = null;

	tools: DrillingEquipmentModel[] = [];

	private _well: Well;

	@Input() set well(value: Well) {
		if (value.casingTubing == null) {
			value.casingTubing = [];
		}
		value.casingTubing.forEach(c => {
			this.currentCasings.push(c);
		});
		this._well = value;
	}

	get well(): Well {
		return this._well;
	}

	sortedCasings: CasingTubing[] = [];

	constructor(private _domainService: DomainDefinitionService, private formatterService: GridFormatterService, private configService: ApplicationConfigService) { }

	ngOnInit() {
		this.gridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.buildColumns());
		this.gridOptions.headerHeight = 50;
		this.gridOptions.onGridReady = (params) => { this.gridApi = params.api; };
		this.gridOptions.getRowClass = (params: any) => {
			if (params.data.comments != null && params.data.comments.length > 0) return 'red';
		};
		const tool$ = this._domainService.getValues("RIG_TYP").pipe(
			tap((tools: any[]) => {
				if (tools != null) {
					tools.forEach(t => {
						const model = new DrillingEquipmentModel();
						model.code = t.Code;
						model.definition = t.Definition;
						model.isActive = t.isRequired;
						model.lastModifiedDateUTC = t.LastModifiedDateUTC;
						this.tools.push(model);
					});
				}
			})
		);

		const casingString$: Observable<any> = this._domainService.getValues("CSG_STRING").pipe(
			tap((casings: any[]) => {
				if (casings != null) {
					casings.forEach(c => {
						const model = new CasingTypeModel();
						model.code = c.Code;
						model.definition = (c.Code !== 'HOL1') ? c.Definition : 'Open Hole';
						model.isActive = c.isActive;
						model.isRequired = c.isRequired;
						model.lastModifiedDateUTC = c.LastModifiedDateUTC;
						this.filteredCasings.push(model);
					});
				}
			})
		);

		const config$: Observable<ModuleConfig> = this.configService.getModuleConfig()
			.pipe(
				tap((conf: ModuleConfig) => {
					if (conf.postDrilling != null && conf.postDrilling.validCasingTypes != null) {
						this._validStringCasingTypeCodes = conf.postDrilling.validCasingTypes;
					}
				})
			)

		this._stringSubscription = forkJoin([casingString$, tool$, config$])
			.pipe(
				finalize(() => {
					this._stringSubscription.unsubscribe();
				})
			)
			.subscribe({
				next: () => {
					if (this.currentCasings != null && this.currentCasings.length > 0 && this._validStringCasingTypeCodes.length > 0) {
						this.sortCasingTubingRecords(this.currentCasings);
					}
					this.dataReady = true;
				},
				error: (err) => {
					console.error(err);
				}
			});
	}

	ngOnDestroy() {
		if (this._stringSubscription != null) {
			this._stringSubscription.unsubscribe();
		}
	}

	buildColumns(): ColDef[] {
		let columns: ColDef[] = [
			{
				headerClass: "wrapped",
				headerName: "Type",
				headerTooltip: "Type",
				field: "type",
				valueFormatter: this.formatCasingType(),
				tooltipValueGetter: this.formatCasingType(),
				filter: false,
				sortable: false,
				minWidth: 125,
				maxWidth: 125,
				pinned: "left"
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Wellbore Diameter",
				headerTooltip: "Wellbore Diameter",
				field: "wellboreDiameter",
				tooltipField: "wellboreDiameter",
				filter: false,
				sortable: false,
				minWidth: 125,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Casing Diameter",
				headerTooltip: "Casing Diameter",
				field: "casingDiameter",
				tooltipField: "casingDiameter",
				filter: false,
				sortable: true,
				sort: 'desc',
				minWidth: 125,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Collar Diameter",
				headerTooltip: "Collar Diameter",
				field: "collarDiameter",
				tooltipField: "collarDiameter",
				filter: false,
				sortable: false,
				minWidth: 125,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Bottom",
				headerTooltip: "Bottom",
				field: "bottom",
				tooltipField: "bottom",
				filter: false,
				sortable: false,
				minWidth: 90,
				maxWidth: 100
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Feet Installed",
				headerTooltip: "Feet Installed",
				field: "feetInstalled",
				tooltipField: "feetInstalled",
				filter: false,
				sortable: false,
				minWidth: 125,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Feet Left In Well",
				headerTooltip: "Feet Left In Well",
				field: "feetLeftInWell",
				tooltipField: "feetLeftInWell",
				filter: false,
				sortable: false,
				minWidth: 90,
				maxWidth: 100
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Cement Volume (Sacks)",
				headerTooltip: "Cement Volume (Sacks)",
				field: "cementVolume",
				tooltipField: "cementVolume",
				filter: false,
				sortable: false,
				minWidth: 90,
				maxWidth: 100
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Cement Top",
				headerTooltip: "Cement Top",
				field: "cementTop",
				tooltipField: "cementTop",
				filter: false,
				sortable: false,
				minWidth: 90,
				maxWidth: 100
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Grade",
				headerTooltip: "Grade",
				field: "grade",
				tooltipField: "grade",
				filter: false,
				sortable: false,
				minWidth: 90,
				maxWidth: 100
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Weight",
				headerTooltip: "Weight",
				field: "weight",
				tooltipField: "weight",
				filter: false,
				sortable: false,
				minWidth: 90,
				maxWidth: 100
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Burst Pressure Rating",
				headerTooltip: "Burst Pressure Rating",
				field: "burstPressureRating",
				tooltipField: "burstPressureRating",
				filter: false,
				sortable: false,
				minWidth: 125,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Tool Type",
				headerTooltip: "Tool Type",
				field: "toolType",
				valueFormatter: this.formatToolType(),
				tooltipValueGetter: this.formatToolType(),
				filter: false,
				sortable: false,
				minWidth: 125,
				maxWidth: 150
			} as ColDef
		];
		if (this.canEdit) {
			let editColumn: ColDef =
				{
					cellStyle: { 'text-align': 'right', 'line-height': 1.75 },
					cellRenderer: IconActionRendererComponent,
					cellRendererParams: {
						buttons: [
							{
								action: (obj: any) => this.editRecord(obj),
								label: 'Edit',
								icon: 'edit'
							}
						]
					},
					filter: false,
					sortable: false,
					minWidth: 50,
					maxWidth: 50,
					pinned: 'left'
				} as ColDef;
			columns.unshift(editColumn);
		}

		return columns;
	}

	editRecord(record: EditedCasingTubingRecord): void {
		if (this.editor == null) {
			console.error('cannot find instance of casing tubing editor');
			return;
		}
		this.editor.openForm(record);
	}

	formatCasingType() {
		return (obj: any) => {
			let value = obj.value;
			if (value != null && this.filteredCasings != null) {
				let casing = this.filteredCasings.find(c => c.code == value);
				if (casing != null) {
					if (casing.definition.endsWith("Casing")) {
						let index = casing.definition.indexOf("Casing");
						value = casing.definition.substring(0, index);
					} else if (casing.definition.endsWith("1") || casing.definition.endsWith("2")) {
						value = casing.definition.substring(0, casing.definition.length - 2);
					} else {
						value = casing.definition;
					}
				}
			}
			return value;
		}
	}

	formatToolType() {
		return (obj: any) => {
			let value = obj.value;
			if (value != null && this.tools != null) {
				let tool = this.tools.find(t => t.code === value);
				if (tool != null) {
					value = tool.definition;
				}
			}
			return value;
		}
	}



	getCasingType(record: CasingTubing): string {
		let description: string = record.type;
		let casing = this.filteredCasings.find(c => c.code === record.type);
		if (casing != null) {
			if (casing.definition.endsWith("Casing")) {
				let index = casing.definition.indexOf("Casing");
				description = casing.definition.substring(0, index);
			} else if (casing.definition.endsWith("1") || casing.definition.endsWith("2")) {
				description = casing.definition.substring(0, casing.definition.length - 2);
			} else {
				description = casing.definition;
			}
		}
		return description;
	}

	getToolType(record: CasingTubing): string {
		let description: string = "";
		if (record.toolType != null) {
			description = record.toolType;
			let tool = this.tools.find(t => t.code === record.toolType);
			if (tool != null) {
				description = tool.definition;
			}
		}
		return description;
	}


	onGridReady(event: any) {
		this.formatterService.gridReadySizeToFit(event);
	}

	recordEdited(edit: EditedCasingTubingRecord): void {
		console.log('record in strings component', edit);
		let index = this.currentCasings.findIndex(c => c.id === edit.id);
		if (index != -1) {
			console.log('before', this.currentCasings);
			this.currentCasings[index] = edit;
			console.log('after', this.currentCasings);
			if (this.gridApi != null) {
				this.gridApi.setGridOption("rowData", []);
				this.gridApi.setGridOption("loading", true);
			}
			setTimeout(() => {
				if (this.gridApi != null) {
					this.gridApi.setGridOption("rowData", this.currentCasings);
					this.gridApi.setGridOption("loading", false);
				}
			}, 500);
		}
		this.casingTubingUpdated.emit(edit);
	}

	sizeColumnsToFit(): void {
		if (this.gridApi != null) {
			this.gridApi.sizeColumnsToFit();
		}
	}

	sortCasingTubingRecords(records: CasingTubing[]): CasingTubing[] {
		let sorted: CasingTubing[] = [];
		this._validStringCasingTypeCodes.forEach(code => {
			let casing = records.filter(r => r.type == code);
			if (casing.length > 0) {
				casing.forEach(c => {
					sorted.push(c);
				});
			}
		});
		if (sorted.length != records.length) {
			records.forEach(r => {
				let index = sorted.findIndex(s => s.id === r.id);
				if (index === -1) {
					sorted.push(r);
				}
			});
		}

		return sorted;
	}

}
